import { ZiroModalService } from '../../shared/services/ziro-modal.service';
import { ToastTypes } from '../../shared/services/abstract/toast.service.abstract';
import { Directive, inject, OnDestroy, OnInit } from '@angular/core';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../shared/bottom-nav/bottom-nav.service';
import { ToastService } from '../../shared/services/toast.service';
import { ZiroIcons } from '../../shared/models/ziro-icons.enum';
import { DatabaseImportRequest } from '../../shared/models/generated/ziroModels';
import { ZiroFormAbstractDirective } from '../../forms/ziro-form-abstract.directive';
import { HtmlInputType, ZiroTextConfig } from '../../forms/fields/text/ziro-text.component';
import { HtmlInputAddOn, ZiroFormConfig, ZiroFormsValidationState } from '../../forms/ziro-forms-models';
import { ZiroFileUploadConfig } from '../../forms/fields/file-upload/ziro-file-upload.component';
import { ZiroRadioConfig } from '../../forms/fields/radio/ziro-radio.component';
import { EMPTY, Observable, Subscription, throwError } from 'rxjs';
import { ButtonStyles, ButtonTypes } from '../../button/button.component';
import { catchError, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export interface AdminImportConfig {
  importFrom: ImportTypes;
  contextUrl: string;
  username: string;
  password: string;
  importFromFile: any;
}

export enum ImportTypes {
  INSTANCE,
  FILE,
}

export enum ImportFileTypes {
  CDR = 'CDR',
  AUDIT = 'AUDIT',
  CONFIG = 'CONFIG',
}

@Directive({
  selector: '[ziroAdminImportAbstract]',
  standalone: false,
})
export abstract class ImportAbstractDirective
  extends ZiroFormAbstractDirective<AdminImportConfig>
  implements OnInit, OnDestroy
{
  protected _toastService = inject(ToastService);
  protected _ziroModalService = inject(ZiroModalService);
  protected _bottomNavService = inject(BottomNavService);
  protected _translateService = inject(TranslateService);

  importFileTypes = ImportFileTypes;
  isLoading = true;
  warningText: string;
  warningTextPreamble: string;
  importFileType: ImportFileTypes;
  ziroIcons = ZiroIcons;
  importFromFileMessage = '';

  private validators = {
    importFromFileValidator: {
      validator: (val: File) => {
        this.importFromFileMessage = '';
        this.importFromFileMessage = this._translateService.instant('tkey;admin.import.error.message', {
          importFileType: this.importFileType.toLowerCase(),
        });
        return val?.name.toLowerCase().includes(this.importFileType.toLowerCase())
          ? ZiroFormsValidationState.VALID
          : ZiroFormsValidationState.INVALID;
      },
      message: () => this.importFromFileMessage,
    },
  };

  requestImport: () => Observable<any>;

  formConfig: ZiroFormConfig = {
    fields: {
      // Import From Radio
      importFrom: {
        label: 'tkey;pages.admin.audit.import.type.label',
        dataAutomation: 'ziro-import-abstract-import-from-input',
        disabled: () => this.isSubmitting,
        componentConfig: new ZiroRadioConfig<any>({
          buttons: [
            {
              value: ImportTypes.INSTANCE,
              label: 'tkey;pages.admin.audit.import.type.instance.label',
            },
            {
              value: ImportTypes.FILE,
              label: 'tkey;pages.admin.audit.import.type.file.label',
            },
          ],
          inline: true,
        }),
      },
      // Text Fields
      contextUrl: {
        label: 'tkey;pages.admin.import.address.label',
        dataAutomation: 'import-from-instance-url',
        disabled: () => this.isSubmitting,
        required: () => !this._isImportFromFile(),
        valExcluded: () => this._isImportFromFile(),
        hidden: () => this._isImportFromFile(),
        componentConfig: new ZiroTextConfig({
          htmlInputType: HtmlInputType.TEXT,
          htmlInputAddOn: {
            prependedContent: 'https://',
          } as HtmlInputAddOn,
        }),
      },
      username: {
        label: 'tkey;pages.admin.import.username.label',
        disabled: () => this.isSubmitting,
        required: () => !this._isImportFromFile(),
        valExcluded: () => this._isImportFromFile(),
        hidden: () => this._isImportFromFile(),
        dataAutomation: 'import-from-instance-username',
      },
      password: {
        label: 'tkey;pages.admin.import.password.label',
        disabled: () => this.isSubmitting,
        required: () => !this._isImportFromFile(),
        valExcluded: () => this._isImportFromFile(),
        hidden: () => this._isImportFromFile(),
        dataAutomation: 'import-from-instance-password',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.PASSWORD }),
      },
      // File Input
      importFromFile: {
        label: '',
        dataAutomation: 'import-from-file-input',
        disabled: () => this.isSubmitting,
        required: () => this._isImportFromFile(),
        valExcluded: () => !this._isImportFromFile(),
        hidden: () => !this._isImportFromFile(),
        componentConfig: new ZiroFileUploadConfig({ acceptedFileExtensions: { acceptedExtensions: '.sql' } }),
        validation: [],
      },
    },
  };

  private _subs = new Subscription();

  protected static formatUrl(initialUrl: string): string {
    return initialUrl.match(/^https?:\/\//) ? initialUrl : `https://${initialUrl}`;
  }

  ngOnInit(): void {
    this.entitySource.next({
      ...this.formData,
      importFrom: ImportTypes.INSTANCE,
    });

    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'importConfigButton',
          label: 'tkey;pages.admin.import.bottom_nav.import.label',
          icon: this.ziroIcons.IMPORT,
          buttonClass: ButtonStyles.PRIMARY,
          dataAutomation: 'bottom-nav-import-button',
          state: {
            pending: false,
            buttonDisableState: { disabled: false, tooltipKey: '' },
          },
          type: ButtonTypes.SUBMIT,
          submitSubject: this._validateAndSubmitSource,
        },
      ]),
    );

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() => {
      this._bottomNavService.dispatch(
        new BottomNavUpdateState({
          hasValidationError: !this.isFormValid(),
        }),
      );
    });
    this._subs.add(formSubmittedSub);

    this.formConfig.fields.importFromFile.validation.push(this.validators.importFromFileValidator);

    this.isLoading = false;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subs.unsubscribe();
  }

  getImportRequest(): DatabaseImportRequest {
    let request;

    if (this.formData.importFrom === ImportTypes.INSTANCE) {
      request = {
        contextUrl: ImportAbstractDirective.formatUrl(this.formData.contextUrl),
        username: this.formData.username,
        password: this.formData.password,
      };
    } else {
      request = this.formData.importFromFile;
    }

    return request;
  }

  protected submit(): Observable<void> {
    return this._onImportClicked();
  }

  private _onImportClicked(): Observable<void> {
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
      }),
    );

    this._setPending(true);

    return this.requestImport().pipe(
      tap(() => {
        this._setPending(false);
        const options = {
          modalViewProperties: {
            title: this._translateService.instant('tkey;pages.admin.app.import.ok.tooltip'),
            icon: ZiroIcons.CIRCLE_CHECK_OUTLINE,
            iconClass: 'text-success',
            displayCloseButton: true,
            buttons: [
              {
                label: 'tkey;dialogs.button.ok',
                buttonClass: ButtonStyles.OUTLINE_PRIMARY,
                dataAutomation: 'confirmation-modal-ok-button',
              },
            ],
          },
        };

        this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
      }),
      catchError((response) => {
        this.isSubmitting = false;
        this._validateAndSubmitSource.next(false);
        this._setPending(false);
        const errorDescription = response ? response.error.description : '';

        if (errorDescription === 'Username or password is incorrect') {
          this._toastService.push(ToastTypes.ERROR, this.ziroIcons.DOWNLOAD, 'Unauthorized', errorDescription);
          return throwError(() => EMPTY);
        } else if (errorDescription === 'User does not have permission to export database') {
          this._toastService.push(ToastTypes.ERROR, this.ziroIcons.DOWNLOAD, 'Forbidden', errorDescription);
          return throwError(() => EMPTY);
        } else if (errorDescription.includes('Could not connect to host')) {
          this._toastService.push(ToastTypes.ERROR, this.ziroIcons.DOWNLOAD, 'Bad Gateway', errorDescription);
          return throwError(() => EMPTY);
        } else if (response === null) {
          this._bottomNavService.dispatch(
            new BottomNavUpdateState({
              hasValidationError: true,
            }),
          );
          return throwError(() => EMPTY);
        } else {
          return throwError(() => response);
        }
      }),
    );
  }

  private _setPending(setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'importConfigButton',
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }

  private _isImportFromFile(): boolean {
    return this.formData.importFrom === ImportTypes.FILE || this.importFileType === ImportFileTypes.CDR;
  }
}
